import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { showToast } from "../../../../utils/showToast";
import {
  getInternalConversation,
  postInternalChat,
  isEndpointMissing,
  isNoInternalAgent,
} from "./internalAssistantApi";

// Cada cuánto se sondea el GET mientras el backend no devolvió la respuesta de
// la IA. El POST ya esperó hasta 25 s, así que esto solo cubre la cola.
const POLL_INTERVAL_MS = 3000;
// Tope de la espera. Pasado esto se deja de sondear y se avisa: la respuesta
// puede llegar igual, pero la pantalla no puede quedarse girando para siempre.
const POLL_TIMEOUT_MS = 60000;
// Mismo tope que valida el backend (1-2000). Acá solo evita un POST que ya
// sabemos que va a rebotar.
export const MAX_MESSAGE_LENGTH = 2000;

/**
 * Normaliza un mensaje del contrato a la forma que pinta la vista.
 * `sender_role` del backend → `role` de la UI; el contenido nunca es undefined
 * (un mensaje sin texto no debe romper el render de la burbuja).
 * @param {object|null|undefined} raw
 * @param {string} [fallbackId]
 * @returns {{id: string, role: 'user'|'ai'|'system', content: string, createdAt: string|null}|null}
 */
export function normalizeMessage(raw, fallbackId) {
  if (!raw) return null;
  const role =
    raw.sender_role === "ai" ? "ai" : raw.sender_role === "system" ? "system" : "user";
  return {
    id: raw.id ?? fallbackId ?? `msg-${role}`,
    role,
    content: typeof raw.content === "string" ? raw.content : "",
    createdAt: raw.createdAt ?? raw.created_at ?? null,
  };
}

/**
 * @param {Array} raw
 * @returns {Array}
 */
export function normalizeMessages(raw) {
  if (!Array.isArray(raw)) return [];
  return raw.map((m, i) => normalizeMessage(m, `msg-${i}`)).filter(Boolean);
}

/**
 * Estado del hilo interno: carga inicial, envío, espera de la respuesta de la
 * IA y los tres modos de degradación (backend ausente, sin agente interno,
 * error real). La vista solo pinta lo que devuelve este hook.
 *
 * `status`:
 *   - "loading"  → GET inicial en vuelo
 *   - "ready"    → hilo disponible (puede estar vacío)
 *   - "missing"  → 404/405: la ruta no está desplegada (PendingBackendNotice)
 *   - "noAgent"  → 409 NO_INTERNAL_AGENT: falta el agente de audiencia interna
 *   - "error"    → fallo real del GET
 * @returns {object}
 */
export default function useInternalChat() {
  const { t } = useTranslation();
  const [status, setStatus] = useState("loading");
  const [agent, setAgent] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [awaiting, setAwaiting] = useState(false);

  // Evita setState tras desmontar: el POST puede tardar hasta 25 s y el usuario
  // puede cambiar de ruta mientras tanto.
  // Se vuelve a poner en true al montar: en dev, React.StrictMode monta,
  // desmonta y vuelve a montar, y un ref que solo se apaga dejaba la vista
  // en "loading" para siempre (el resultado del GET se descartaba).
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Mensajes de IA ya vistos al enviar. Es la referencia para saber si el
  // sondeo trajo una respuesta NUEVA: comparar por fecha dependería de que el
  // backend devuelva createdAt con la misma precisión en POST y en GET.
  const aiCountRef = useRef(0);
  const tempSeqRef = useRef(0);

  // `t` en una ref para que el efecto de sondeo NO la lleve en las
  // dependencias: useTranslation le cambia la identidad al cambiar de idioma, y
  // eso reiniciaría el intervalo (y con él la ventana de 60 s) sin motivo.
  const tRef = useRef(t);
  tRef.current = t;

  const load = useCallback(async () => {
    setStatus("loading");
    try {
      const data = await getInternalConversation();
      if (!mountedRef.current) return;
      setAgent(data?.agent ?? null);
      setMessages(normalizeMessages(data?.messages));
      setStatus("ready");
    } catch (err) {
      if (!mountedRef.current) return;
      setMessages([]);
      // El contrato pone el 409 en el POST, pero si el backend también lo
      // devuelve acá, el usuario merece la CTA al editor de agentes en vez de
      // un "Reintentar" que va a fallar igual.
      if (isEndpointMissing(err)) setStatus("missing");
      else if (isNoInternalAgent(err)) setStatus("noAgent");
      else setStatus("error");
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // ── Sondeo de la respuesta pendiente ──────────────────────────────────────
  // El backend devuelve reply en el POST salvo que se le acabe el tiempo de
  // espera de la IA (25 s). En ese caso se vuelve a pedir el hilo cada 3 s
  // hasta que aparezca un mensaje de IA nuevo o se agote el minuto.
  useEffect(() => {
    if (!awaiting) return undefined;
    const startedAt = Date.now();
    let cancelled = false;

    const tick = async () => {
      if (cancelled) return;
      if (Date.now() - startedAt >= POLL_TIMEOUT_MS) {
        setAwaiting(false);
        showToast(tRef.current("La respuesta está tardando más de lo esperado."), "warning");
        return;
      }
      try {
        const data = await getInternalConversation();
        if (cancelled || !mountedRef.current) return;
        const next = normalizeMessages(data?.messages);
        setMessages(next);
        if (next.filter((m) => m.role === "ai").length > aiCountRef.current) {
          setAwaiting(false);
        }
      } catch {
        // Un sondeo que falla no dice nada del mensaje ya aceptado: se
        // reintenta en el siguiente tick y el tope del minuto cierra la espera.
      }
    };

    const timer = setInterval(tick, POLL_INTERVAL_MS);
    tick();
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [awaiting]);

  const send = useCallback(async () => {
    const text = draft.trim();
    if (!text || sending || awaiting) return;

    const tempId = `tmp-${(tempSeqRef.current += 1)}`;
    aiCountRef.current = messages.filter((m) => m.role === "ai").length;
    // Burbuja optimista: el POST puede tardar 25 s y el mensaje propio tiene
    // que verse al instante. El sondeo la reemplaza con la copia del backend.
    setMessages((prev) => [
      ...prev,
      { id: tempId, role: "user", content: text, createdAt: null, pending: true },
    ]);
    setDraft("");
    setSending(true);

    try {
      const data = await postInternalChat(text);
      if (!mountedRef.current) return;
      const saved =
        normalizeMessage(data?.message, tempId) ?? {
          id: tempId,
          role: "user",
          content: text,
          createdAt: null,
        };
      setMessages((prev) => prev.map((m) => (m.id === tempId ? saved : m)));

      const reply = normalizeMessage(data?.reply);
      if (reply) setMessages((prev) => [...prev, reply]);
      else setAwaiting(true);
    } catch (err) {
      if (!mountedRef.current) return;
      // El mensaje no llegó: se quita la burbuja y se devuelve el texto al
      // redactor para que el usuario pueda reintentar sin reescribirlo.
      setMessages((prev) => prev.filter((m) => m.id !== tempId));
      setDraft(text);
      if (isNoInternalAgent(err)) setStatus("noAgent");
      else if (isEndpointMissing(err)) setStatus("missing");
      else showToast(err?.message || t("No se pudo enviar el mensaje."), "error");
    } finally {
      if (mountedRef.current) setSending(false);
    }
  }, [draft, sending, awaiting, messages, t]);

  // Reintento manual desde los estados de degradación. Un `noAgent` se resuelve
  // creando el agente en otra pestaña: el GET vuelve a decidir el estado.
  const retry = useCallback(() => {
    load();
  }, [load]);

  return {
    status,
    agent,
    messages,
    draft,
    setDraft,
    sending,
    awaiting,
    send,
    retry,
    maxLength: MAX_MESSAGE_LENGTH,
  };
}
